import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import type { Flow } from '../../core/domain/models'
import { FlowIcon } from '../components/FlowIcon'
import { PencilIcon } from '../components/Icons'

interface FlowToolbarProps {
  flows: Flow[]
  selectedFlowId: string
  onSelectFlow: (flowId: string) => void
  onOpenFlowsModal: () => void
  scenarioId: string
  listId: string
}

export function FlowToolbar({
  flows,
  selectedFlowId,
  onSelectFlow,
  onOpenFlowsModal,
  scenarioId,
  listId,
}: FlowToolbarProps) {
  const navigate = useNavigate()
  const flowChipsRef = useRef<HTMLDivElement | null>(null)

  // Scroll selected flow chip into view (on selection change or when flows load)
  useEffect(() => {
    if (selectedFlowId && flowChipsRef.current) {
      const timeoutId = setTimeout(() => {
        const activeChip = flowChipsRef.current?.querySelector('.flow-chip-active') as HTMLElement
        if (activeChip) {
          activeChip.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
        }
      }, 50)
      return () => clearTimeout(timeoutId)
    }
  }, [selectedFlowId, flows])

  return (
    <div className="list-flow-toolbar">
      {flows.length > 0 && (
        <div className="flow-chips-container" ref={flowChipsRef}>
          {flows.map((f: any) => (
            <button
              key={f.id}
              onClick={() => onSelectFlow(selectedFlowId === f.id ? '' : f.id)}
              className={`flow-chip ${selectedFlowId === f.id ? 'flow-chip-active' : ''}`}
              data-flow-color={f.color ?? ''}
              title={selectedFlowId === f.id ? 'Click to deselect' : `Use ${f.name} flow`}
              aria-pressed={selectedFlowId === f.id}
            >
              {f.name}
            </button>
          ))}
        </div>
      )}

      <div className="flow-action-buttons">
        {selectedFlowId && (
          <button
            type="button"
            className="flow-action-btn"
            onClick={() =>
              navigate(`/scenario/${scenarioId}/flow/${selectedFlowId}`, {
                state: { backTo: `/scenario/${scenarioId}/list/${listId}?flow=${selectedFlowId}` },
              })
            }
            title="Edit flow ordering and availability"
            aria-label="Edit flow ordering and availability"
          >
            <PencilIcon />
          </button>
        )}
        <button
          type="button"
          className="flow-action-btn"
          onClick={onOpenFlowsModal}
          title="Flows"
          aria-label="Flows"
        >
          <FlowIcon size={16} />
        </button>
      </div>
    </div>
  )
}
